import type { PlanEvent, Task, TaskId, TaskStatus, ViewMode } from "../../shared/types";
import { UndoToast } from "../atoms/UndoToast";
import { SearchModal } from "../../features/search/components/SearchModal";
import { FocusMode } from "../../features/pomodoro/components/FocusMode";
import { KeyboardShortcutsHelp } from "../../features/settings/components/KeyboardShortcutsHelp";
import { AdminPanel } from "../../features/admin/components/AdminPanel";
import { AchievementToast } from "../../features/gamification/components/AchievementToast";
import { useAchievementToast } from "../../features/gamification/hooks/use-achievement-toast";
import { RecurrenceScopeDialog } from "../../features/calendar/components/dialogs/RecurrenceScopeDialog";
import { useModals } from "../../hooks/useModals";

interface PendingDelete {
  readonly message: string;
}

interface UndoDeleteState {
  readonly pending: PendingDelete | null;
  readonly undo: () => void;
  readonly dismiss: () => void;
}

interface SearchState {
  readonly query: string;
  readonly setQuery: (q: string) => void;
  readonly results: { tasks: Task[]; events: PlanEvent[] };
  readonly isOpen: boolean;
  readonly close: () => void;
}

interface AppModalsProps {
  readonly search: SearchState;
  readonly focusTask: Task | null;
  readonly taskUndoDelete: UndoDeleteState;
  readonly eventUndoDelete: UndoDeleteState;
  readonly onSetViewMode: (mode: ViewMode) => void;
  readonly onCycleStatus: (id: TaskId) => void;
  readonly onCompleteFocus: () => void;
  readonly onUpdateTaskStatus?: (id: TaskId, status: TaskStatus) => void;
  readonly pendingRecurrenceDelete: PlanEvent | null;
  readonly onRecurrenceDeleteChoice: (action: "this" | "all") => void;
  readonly onCancelRecurrenceDelete: () => void;
}

export function AppModals({
  search,
  focusTask,
  taskUndoDelete,
  eventUndoDelete,
  onSetViewMode,
  onCycleStatus,
  onCompleteFocus,
  onUpdateTaskStatus,
  pendingRecurrenceDelete,
  onRecurrenceDeleteChoice,
  onCancelRecurrenceDelete,
}: AppModalsProps) {
  const achievementToast = useAchievementToast();
  const modals = useModals();

  return (
    <>
      {achievementToast.toasts.length > 0 && (
        <div className="fixed inset-x-0 top-6 z-[60] flex flex-col items-center gap-3 pointer-events-none">
          {achievementToast.toasts.map((toast) => (
            <AchievementToast
              key={toast.id}
              achievement={toast.achievement}
              onDismiss={() => achievementToast.dismiss(toast.id)}
            />
          ))}
        </div>
      )}

      {search.isOpen && (
        <SearchModal
          query={search.query}
          onQueryChange={search.setQuery}
          results={search.results}
          onClose={search.close}
          onNavigate={onSetViewMode}
          onUpdateTaskStatus={onUpdateTaskStatus}
        />
      )}

      {modals.showFocus && focusTask && (
        <FocusMode
          task={focusTask}
          onComplete={() => {
            onCycleStatus(focusTask.id);
            onCompleteFocus();
          }}
          onSkip={() => modals.setShowFocus(false)}
          onClose={() => modals.setShowFocus(false)}
        />
      )}

      {modals.showShortcuts && (
        <KeyboardShortcutsHelp
          isOpen={modals.showShortcuts}
          onClose={() => modals.setShowShortcuts(false)}
        />
      )}

      {modals.showAdmin && <AdminPanel onClose={() => modals.setShowAdmin(false)} />}

      {taskUndoDelete.pending && (
        <UndoToast
          message={taskUndoDelete.pending.message}
          onUndo={taskUndoDelete.undo}
          onDismiss={taskUndoDelete.dismiss}
        />
      )}

      {eventUndoDelete.pending && (
        <UndoToast
          message={eventUndoDelete.pending.message}
          onUndo={eventUndoDelete.undo}
          onDismiss={eventUndoDelete.dismiss}
        />
      )}

      <RecurrenceScopeDialog
        open={pendingRecurrenceDelete !== null}
        mode="delete"
        onThis={() => onRecurrenceDeleteChoice("this")}
        onAll={() => onRecurrenceDeleteChoice("all")}
        onCancel={onCancelRecurrenceDelete}
      />
    </>
  );
}
